<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Redirección de reembolsos</h1>
      <v-btn rounded color="primary" depressed @click="openDialog(null)">
        <v-icon left>mdi-plus</v-icon>Agregar perfil
      </v-btn>
    </v-row>
    <p class="text-body-2 grey--text text--darken-1">
      Contrapartes que atienden los reembolsos directamente. Los plan-servicios con un perfil asignado
      muestran sus contactos en la app en vez del formulario de reembolso.
    </p>

    <v-dialog :value="showDialog" max-width="900" @input="onDialogInput">
      <refund-redirect-profile-maintainer
        v-if="showDialog"
        :profile="selectedProfile"
        @saved="onSaved"
        @close-dialog="closeDialog"
      ></refund-redirect-profile-maintainer>
    </v-dialog>

    <v-dialog :value="showDelete" :persistent="deleting" max-width="520" @input="onDeleteInput">
      <v-card v-if="profileToDelete">
        <v-card-title class="text-h6 font-weight-bold">Eliminar perfil</v-card-title>
        <v-card-text>
          ¿Eliminar el perfil de <strong>{{ profileToDelete.counterpartName }}</strong>? Esta acción no se puede deshacer.
          <v-alert v-if="deleteError" type="error" dense outlined class="mt-4 mb-0">{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="deleting" @click="closeDelete">Cancelar</v-btn>
          <v-btn color="red" dark depressed :loading="deleting" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      :headers="headers"
      :items="profiles"
      :loading="loading"
      item-key="id"
      class="elevation-1"
      no-data-text="No hay perfiles de redirección."
      :footer-props="{ 'items-per-page-options': [10, 20, 50] }"
    >
      <template v-slot:item.counterpartName="{ item }">
        <div class="d-flex align-center">
          <v-img v-if="item.logoUrl" :src="item.logoUrl" max-width="32" max-height="32" contain class="mr-2"></v-img>
          <span class="font-weight-medium">{{ item.counterpartName }}</span>
        </div>
      </template>
      <template v-slot:item.channelCount="{ item }">
        {{ (item.channels || []).length }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mx-1" fab dark depressed x-small color="blue" v-bind="attrs" v-on="on" @click="openDialog(item)">
              <v-icon small dark>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Editar</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mx-1" fab dark depressed x-small color="red" v-bind="attrs" v-on="on" @click="openDelete(item)">
              <v-icon small dark>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>Eliminar</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import RefundRedirectProfileMaintainer from '../components/RefundRedirectProfileMaintainer.vue';
import RefundRedirectProfilesService from '../services/refund-redirect-profiles.service';

const PROFILE_IN_USE = 'REFUND_REDIRECT_PROFILE_IN_USE';

export default {
  name: 'RefundRedirectProfiles',
  components: { RefundRedirectProfileMaintainer },
  data() {
    return {
      headers: [
        { text: 'Contraparte', value: 'counterpartName' },
        { text: 'Título', value: 'title', sortable: false },
        { text: 'Canales', value: 'channelCount', align: 'end', sortable: false },
        { text: 'Plan-servicios', value: 'servicePlanCount', align: 'end' },
        { text: 'Acciones', value: 'actions', align: 'end', sortable: false }
      ],
      profiles: [],
      loading: false,
      showDialog: false,
      selectedProfile: null,
      showDelete: false,
      profileToDelete: null,
      deleting: false,
      deleteError: '',
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  created() {
    this.fetchProfiles();
  },
  methods: {
    async fetchProfiles() {
      this.loading = true;
      const res = await RefundRedirectProfilesService.getAll();
      this.loading = false;
      if (res.status !== 'ok') {
        this.notify('No se pudieron cargar los perfiles de redirección.', 'error');
        return;
      }
      this.profiles = res.data || [];
    },
    openDialog(profile) {
      this.selectedProfile = profile;
      this.showDialog = true;
    },
    closeDialog() {
      this.showDialog = false;
      this.selectedProfile = null;
    },
    // Clic fuera o Esc: v-dialog emite input(false); el mantenedor se desmonta (v-if) y pierde su estado.
    onDialogInput(open) {
      if (!open) this.closeDialog();
    },
    onSaved() {
      this.notify('Perfil guardado.', 'success');
      this.fetchProfiles();
    },
    openDelete(profile) {
      this.profileToDelete = profile;
      this.deleteError = '';
      this.showDelete = true;
    },
    closeDelete() {
      if (this.deleting) return;
      this.showDelete = false;
      this.profileToDelete = null;
      this.deleteError = '';
    },
    onDeleteInput(open) {
      if (!open) this.closeDelete();
    },
    deleteErrorMessage(res) {
      // El 409 nombra los planes que usan el perfil: se muestra tal cual.
      if (res.code === PROFILE_IN_USE && res.message) return res.message;
      if (res.httpCode === 404) return 'El perfil ya no existe.';
      return 'No se pudo eliminar el perfil, intenta más tarde.';
    },
    async confirmDelete() {
      this.deleting = true;
      this.deleteError = '';
      const res = await RefundRedirectProfilesService.remove(this.profileToDelete.id);
      this.deleting = false;
      if (res.status !== 'ok') {
        this.deleteError = this.deleteErrorMessage(res);
        return;
      }
      this.closeDelete();
      this.notify('Perfil eliminado.', 'success');
      this.fetchProfiles();
    },
    notify(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  }
};
</script>
